import {
  AvatarShot,
  FixedScriptSpeech,
  SilenceSpeech,
  SoundEffectSpeech,
  TemplateItem,
} from "types";
import {
  CLOSING_DURATION_PLACEHOLDER,
  CLOSING_VIDEO_PLACEHOLDER,
  DEFAULT_SPEECH_MAX_WORDS,
  DEFAULT_SUBTITLES_LINE_HEIGHT,
  DEFAULT_TRANSITION_DURATION_IN_FRAMES,
  INTRODUCTION_DURATION_PLACEHOLDER,
  INTRODUCTION_VIDEO_PLACEHOLDER,
  MAX_CLOSING_DURATION_IN_FRAMES,
  MAX_INTRODUCTION_DURATION_IN_FRAMES,
  MAX_TRANSITION_DURATION_IN_FRAMES,
} from "../constants";
import {
  BodyBackground,
  CustomText,
  Header,
  ItemWithLayerIndex,
  Logo,
  MediaItem,
  Avatar,
  Shape,
  SubtitlesItem,
  TemplateState,
  TemplateType,
  TitleItem,
  VideoScene,
} from "../types";
import {
  defaultAnchor,
  generateFullScreenAttributes,
  generateHeaderAttributes,
  generateItemHeaderGraphics,
  logoPlaceholder,
  sortItemsByLayerIndex,
  generateCustomAssetVideo,
  generateCommonItems,
  generateImage,
  generateCommonAttributes,
  getBackgroundBase,
} from "./mapTemplateStateToServerTemplate";
import { generateTextGraphics } from "./generators/graphicGenerators/Text";
import {
  generatePresenterItems,
  hasVisibleAvatar,
  mapToServerColor,
} from "./itemsHelper";
import { sanitizeText } from "utils/text";
import { withFixedScriptIfNeeded } from ".";

const getIntroductionItems = ({
  storeIntro,
  storeBackground,
  images,
  videos,
  customTexts,
  shapes,
}: {
  storeIntro: VideoScene;
  storeBackground: BodyBackground;
  images: MediaItem[];
  videos: MediaItem[];
  customTexts: CustomText[];
  shapes: Shape[];
}) => {
  //Ensure that background opacity is displayed correctly if applicable

  const items: TemplateItem[] = [getBackgroundBase()];

  if (!storeIntro.assetId) return items;
  const introItem: TemplateItem = {
    graphics: generateCustomAssetVideo(
      storeIntro.assetId,
      undefined,
      undefined,
      null,
      !storeIntro.playVideoSound
    ),
    attributes: generateFullScreenAttributes(),
    ...defaultAnchor,
  };

  items.push(introItem);

  const { bodyRectangle, imageItems, videoItems, customTextItems, shapeItems } =
    generateCommonItems({
      storeBackground,
      images,
      videos,
      customTexts,
      shapes,
    });
  if (bodyRectangle) {
    items.push(bodyRectangle);
  }

  const sortedItems = sortItemsByLayerIndex([
    ...imageItems,
    ...videoItems,
    ...customTextItems,
    ...shapeItems,
  ]);

  items.push(...sortedItems);
  return items;
};

const getTransitionItems = ({
  storeTransition,
  storeBackground,
  titleStyle,
  presenter,
  images,
  videos,
  customTexts,
  shapes,
  aspectRatio,
}: {
  storeTransition: VideoScene;
  storeBackground: BodyBackground;
  titleStyle: TitleItem;
  presenter: Avatar;
  images: MediaItem[];
  videos: MediaItem[];
  customTexts: CustomText[];
  shapes: Shape[];
  aspectRatio: number;
}) => {
  const items: TemplateItem[] = [getBackgroundBase()];

  let transitionItem: TemplateItem | undefined;

  if (storeTransition.view === "Video" && storeTransition.assetId) {
    transitionItem = {
      graphics: generateCustomAssetVideo(storeTransition.assetId),
      attributes: generateFullScreenAttributes(),
      ...defaultAnchor,
    };
  } else if (storeTransition.view === "Picture" && storeTransition.assetId) {
    transitionItem = {
      graphics: generateImage(storeTransition.assetId, "Cover"),
      attributes: generateFullScreenAttributes(),
      ...defaultAnchor,
    };
  }

  if (transitionItem) {
    items.push(transitionItem);
  }
  const { bodyRectangle, imageItems, videoItems, customTextItems, shapeItems } =
    generateCommonItems({
      storeBackground,
      images,
      videos,
      customTexts,
      shapes,
    });

  if (bodyRectangle) {
    items.push(bodyRectangle);
  }

  let titleItem: ItemWithLayerIndex | undefined;

  if (titleStyle.showTitle) {
    if (titleStyle.fontAssetId && titleStyle.color) {
      titleItem = {
        item: {
          graphics: generateTextGraphics(titleStyle),
          attributes: generateCommonAttributes(
            titleStyle.position.x,
            titleStyle.position.y,
            titleStyle.size.width,
            undefined,
            titleStyle.animations,
            undefined,
            titleStyle.rotation
          ),
          ...defaultAnchor,
        },
        layerIndex: titleStyle.layerIndex,
      };
    }
  }

  const presenterItems = generatePresenterItems(presenter, aspectRatio);

  const sortedItems = sortItemsByLayerIndex([
    ...imageItems,
    ...videoItems,
    titleItem,
    ...customTextItems,
    ...shapeItems,
    presenterItems,
  ]);

  items.push(...sortedItems);

  return items;
};

const getChapterItems = ({
  storeHeader,
  storeBackground,
  textStyle,
  presenter,
  logo,
  images,
  videos,
  customTexts,
  shapes,
  aspectRatio,
}: {
  storeHeader: Header;
  storeBackground: BodyBackground;
  textStyle: SubtitlesItem;
  presenter: Avatar;
  logo: Logo;
  images: MediaItem[];
  videos: MediaItem[];
  customTexts: CustomText[];
  shapes: Shape[];
  aspectRatio: number;
}) => {
  const items: TemplateItem[] = [getBackgroundBase()];

  let header: ItemWithLayerIndex | undefined;

  if (
    (storeHeader.background || storeHeader.banner?.assetId) &&
    logo.showLogo &&
    logo.assetId
  ) {
    header = {
      item: {
        name: "header",
        graphics: generateItemHeaderGraphics(storeHeader),
        attributes: generateHeaderAttributes(
          storeHeader.size.width,
          storeHeader.size.height,
          storeHeader.position.y
        ),
        ...defaultAnchor,
      },
      layerIndex: storeHeader.layerIndex,
    };
  }

  let logoItem: ItemWithLayerIndex | undefined;

  if (logo.showLogo && logo.assetId) {
    logoItem = {
      item: {
        name: "logo",
        graphics: generateImage(logoPlaceholder),
        attributes: generateCommonAttributes(
          logo.position.x,
          logo.position.y,
          logo.size.width,
          logo.size.height,
          undefined,
          undefined,
          logo.rotation
        ),
        ...defaultAnchor,
      },
      layerIndex: logo.layerIndex,
    };
  }

  let bodyGraphics: TemplateItem["graphics"] | undefined;

  if (storeBackground.bodyView === "Video" && storeBackground.bodyVideoAsset) {
    bodyGraphics = generateCustomAssetVideo(storeBackground.bodyVideoAsset);
  } else if (
    storeBackground.bodyView === "Picture" &&
    storeBackground.bodyImageAsset
  ) {
    bodyGraphics = generateImage(storeBackground.bodyImageAsset, "Cover");
  }

  if (bodyGraphics) {
    const body: TemplateItem = {
      graphics: bodyGraphics,
      attributes: generateFullScreenAttributes(),
      ...defaultAnchor,
    };

    items.push(body);
  }

  const { bodyRectangle, imageItems, videoItems, customTextItems, shapeItems } =
    generateCommonItems({
      storeBackground,
      images,
      videos,
      customTexts,
      shapes,
    });
  if (bodyRectangle) {
    items.push(bodyRectangle);
  }

  let textItem: ItemWithLayerIndex | undefined;

  if (textStyle.showSubtitles) {
    if (textStyle.fontAssetId && textStyle.color) {
      textItem = {
        item: {
          graphics: textStyle.animated
            ? generateRichSubtitles(
                textStyle.color,
                textStyle.fontAssetId,
                textStyle.fontSize,
                textStyle.strokeColor,
                textStyle.backgroundColor,
                textStyle.lineHeight
              )
            : generateBasicSubtitles(
                textStyle.color,
                textStyle.fontAssetId,
                textStyle.fontSize,
                textStyle.backgroundColor
              ),
          attributes: generateCommonAttributes(
            textStyle.position.x,
            textStyle.position.y,
            textStyle.size.width,
            undefined,
            textStyle.animations,
            undefined,
            textStyle.rotation
          ),
          ...defaultAnchor,
        },
        layerIndex: textStyle.layerIndex,
      };
    }
  }

  const presenterItems = generatePresenterItems(presenter, aspectRatio);

  const sortedItems = sortItemsByLayerIndex([
    header,
    logoItem,
    ...imageItems,
    ...videoItems,
    textItem,
    ...customTextItems,
    ...shapeItems,
    presenterItems,
  ]);

  items.push(...sortedItems);

  return items;
};

const getClosingItems = ({
  storeClosing,
  storeBackground,
  images,
  videos,
  customTexts,
  shapes,
}: {
  storeClosing: VideoScene;
  storeBackground: BodyBackground;
  images: MediaItem[];
  videos: MediaItem[];
  customTexts: CustomText[];
  shapes: Shape[];
}) => {
  const items: TemplateItem[] = [getBackgroundBase()];

  if (!storeClosing.assetId) return items;
  const closingItem: TemplateItem = {
    graphics: generateCustomAssetVideo(
      storeClosing.assetId,
      undefined,
      undefined,
      null,
      !storeClosing.playVideoSound
    ),
    attributes: generateFullScreenAttributes(),
    ...defaultAnchor,
  };

  items.push(closingItem);

  const { bodyRectangle, imageItems, videoItems, customTextItems, shapeItems } =
    generateCommonItems({
      storeBackground,
      images,
      videos,
      customTexts,
      shapes,
    });

  if (bodyRectangle) {
    items.push(bodyRectangle);
  }

  const sortedItems = sortItemsByLayerIndex([
    ...imageItems,
    ...videoItems,
    ...customTextItems,
    ...shapeItems,
  ]);

  items.push(...sortedItems);

  return items;
};

export const generateTemplate = (
  templateState: TemplateState,
  type: TemplateType,
  topic: string,
  aspectRatio: number
) => {
  const {
    images,
    videos,
    customTexts,
    shapes,
    background: storeBackground,
    intro: storeIntro,
    transition: storeTransition,
    titleStyle,
    textStyle,
    presenter,
    closing: storeClosing,
    logo,
    header: storeHeader,
  } = templateState;

  const commonParams = {
    storeBackground,
    images,
    videos,
    customTexts,
    shapes,
  };

  const getItems = (): TemplateItem[] => {
    switch (type) {
      case TemplateType.INTRODUCTION:
        return getIntroductionItems({
          storeIntro,
          ...commonParams,
        });

      case TemplateType.TRANSITION:
        return getTransitionItems({
          storeTransition,
          titleStyle,
          presenter,
          ...commonParams,
          aspectRatio,
        });

      case TemplateType.CHAPTER:
        return getChapterItems({
          storeHeader,
          textStyle,
          presenter,
          logo,
          ...commonParams,
          aspectRatio,
        });

      case TemplateType.CLOSING:
        return getClosingItems({
          storeClosing,
          ...commonParams,
        });

      default:
        return [];
    }
  };

  let avatarShot: AvatarShot = null;

  if (
    (type === TemplateType.CHAPTER || type === TemplateType.TRANSITION) &&
    hasVisibleAvatar(presenter)
  ) {
    avatarShot = presenter.avatarShot;
  }

  const templateName = `${type === TemplateType.TRANSITION ? "Transition-" : ""}${sanitizeText(topic)}`;
  const speech = generateSpeech(
    type,
    storeIntro,
    storeClosing,
    withFixedScriptIfNeeded(storeTransition, hasVisibleAvatar(presenter))
  );

  const isVideoSoundOn =
    (type === TemplateType.INTRODUCTION && storeIntro.playVideoSound) ||
    (type === TemplateType.CLOSING && storeClosing.playVideoSound);

  return {
    name: templateName,
    music: isVideoSoundOn ? { volume: 0 } : templateState.music,
    items: getItems(),
    avatar_shot: avatarShot,
    speech,
  };
};

const generateBasicSubtitles = (
  color: string,
  fontAssetId: string,
  fontSize: number,
  textBackground?: string
): TemplateItem["graphics"] => {
  return {
    type: "Subtitles",
    color: mapToServerColor(color),
    background: textBackground ? mapToServerColor(textBackground) : null,
    font: fontAssetId,
    size: fontSize,
    upper_case: false,
  };
};

const generateRichSubtitles = (
  color: string,
  fontAssetId: string,
  fontSize: number,
  strokeColor?: string,
  textBackground?: string,
  lineHeight?: number
): TemplateItem["graphics"] => {
  const richSubtitles: TemplateItem["graphics"] = {
    type: "RichSubtitles",
    text: {
      color: mapToServerColor(color),
      font: fontAssetId,
      size: fontSize,
      upper_case: false,
      blurry_background: false,
      stroke_color: strokeColor ? mapToServerColor(strokeColor) : null,
      background: textBackground ? mapToServerColor(textBackground) : null,
    },
    line_spacing: lineHeight ?? DEFAULT_SUBTITLES_LINE_HEIGHT,
    highlights: {
      color: mapToServerColor(color),
      font: fontAssetId,
      size: fontSize,
      upper_case: false,
      blurry_background: false,
      stroke_color: null,
      background: null,
    },
  };

  return richSubtitles;
};

const resolveSpeechDuration = (
  item: VideoScene,
  max: number,
  placeholder: string | null
): number | string => {
  if (item.duration !== undefined) return Math.min(item.duration, max);
  if (placeholder && item.assetId === placeholder) {
    return placeholder === INTRODUCTION_VIDEO_PLACEHOLDER
      ? INTRODUCTION_DURATION_PLACEHOLDER
      : CLOSING_DURATION_PLACEHOLDER;
  }
  if (item.assetDuration !== undefined)
    return Math.min(item.assetDuration, max);
  if (placeholder === INTRODUCTION_VIDEO_PLACEHOLDER)
    return INTRODUCTION_DURATION_PLACEHOLDER;
  if (placeholder === CLOSING_VIDEO_PLACEHOLDER)
    return CLOSING_DURATION_PLACEHOLDER;
  return DEFAULT_TRANSITION_DURATION_IN_FRAMES;
};

const generateSpeech = (
  type: TemplateType,
  storeIntro: VideoScene,
  storeClosing: VideoScene,
  storeTransition: VideoScene
): SilenceSpeech | FixedScriptSpeech | SoundEffectSpeech | undefined => {
  const commonSpeechProperties = {
    max_subtitles_words: DEFAULT_SPEECH_MAX_WORDS,
    type: "Silence" as const,
  };

  if (type === TemplateType.INTRODUCTION) {
    if (storeIntro.playVideoSound && storeIntro.assetId) {
      return {
        type: "SoundEffect",
        asset_id: storeIntro.assetId,
        max_subtitles_words: DEFAULT_SPEECH_MAX_WORDS,
      };
    }
    return {
      ...commonSpeechProperties,
      duration: resolveSpeechDuration(
        storeIntro,
        MAX_INTRODUCTION_DURATION_IN_FRAMES,
        INTRODUCTION_VIDEO_PLACEHOLDER
      ),
    };
  }
  if (type === TemplateType.CLOSING) {
    if (storeClosing.playVideoSound && storeClosing.assetId) {
      return {
        type: "SoundEffect",
        asset_id: storeClosing.assetId,
        max_subtitles_words: DEFAULT_SPEECH_MAX_WORDS,
      };
    }
    return {
      ...commonSpeechProperties,
      duration: resolveSpeechDuration(
        storeClosing,
        MAX_CLOSING_DURATION_IN_FRAMES,
        CLOSING_VIDEO_PLACEHOLDER
      ),
    };
  }
  if (type === TemplateType.TRANSITION) {
    if (storeTransition.speechType === "FixedScript") {
      return {
        type: "FixedScript",
        text: "{chapter.topic}",
        max_subtitles_words: DEFAULT_SPEECH_MAX_WORDS,
      };
    }
    return {
      ...commonSpeechProperties,
      duration: resolveSpeechDuration(
        storeTransition,
        MAX_TRANSITION_DURATION_IN_FRAMES,
        null
      ),
    };
  }
  return undefined;
};
